import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthMarketingService, GrowthReviewQueueItem } from './growth-marketing.service';

@Component({
  standalone: true,
  selector: 'ain-growth-content-review-queue',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, DialogModule, InputTextModule, AinDatePipe, RouterLink],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.marketing.reviewQueue.title')" [description]="t('growth.marketing.reviewQueue.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.marketing.reviewQueue.help.page.title'"
        [descriptionKey]="'growth.marketing.reviewQueue.help.page.description'"
        [bulletKeys]="[
          'growth.marketing.reviewQueue.help.page.bullets.approval',
          'growth.marketing.reviewQueue.help.page.bullets.rejectReason',
          'growth.marketing.reviewQueue.help.page.bullets.publish'
        ]" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (items().length === 0) {
          <ain-state type="empty" [title]="t('growth.marketing.reviewQueue.empty')" />
        } @else {
          <p-table [value]="items()" [rows]="20" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.marketing.reviewQueue.columns.type') }}</th>
                <th>{{ t('growth.marketing.reviewQueue.columns.title') }}</th>
                <th>{{ t('growth.marketing.reviewQueue.columns.status') }}</th>
                <th>{{ t('growth.marketing.reviewQueue.columns.scheduledAt') }}</th>
                <th>{{ t('growth.marketing.reviewQueue.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.itemType }}</td>
                <td>{{ row.title }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.scheduledAtUtc ? (row.scheduledAtUtc | ainDate:'datetime') : '-' }}</td>
                <td class="actions">
                  @if (row.workflowProcessInstanceId) {
                    @if (row.workflowTaskId) {
                      <a pButton class="p-button-sm" [routerLink]="['/workflow/tasks', row.workflowTaskId]">{{ t('growth.marketing.reviewQueue.actions.openWorkflowTask') }}</a>
                    } @else {
                      <span class="workflow-pending">{{ t('growth.marketing.reviewQueue.workflowPending') }}</span>
                    }
                  } @else {
                    <button pButton type="button" size="small" [label]="t('growth.marketing.reviewQueue.actions.approve')" (click)="approve(row)"></button>
                    <button pButton type="button" size="small" severity="danger" [label]="t('growth.marketing.reviewQueue.actions.reject')" (click)="openRejectDialog(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <p-dialog [header]="t('growth.marketing.reviewQueue.dialogs.reject')" [visible]="rejectDialogVisible()" (visibleChange)="rejectDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(560px,92vw)' }">
        <div class="card">
          <label>{{ t('growth.marketing.reviewQueue.fields.reason') }}</label>
          <input pInputText [(ngModel)]="rejectReason" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="rejectDialogVisible.set(false)"></button>
          <button pButton type="button" severity="danger" [label]="t('growth.marketing.reviewQueue.actions.reject')" (click)="confirmReject()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
    .workflow-pending { font-size: 12px; color: #6b7280; }
  `]
})
export class GrowthContentReviewQueueComponent implements OnInit {
  private readonly service = inject(GrowthMarketingService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly items = signal<GrowthReviewQueueItem[]>([]);
  readonly rejectDialogVisible = signal(false);
  rejectReason = '';
  private selectedRejectItem: GrowthReviewQueueItem | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service.getReviewQueue().subscribe({
      next: rows => {
        this.items.set(rows);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  approve(item: GrowthReviewQueueItem): void {
    if (item.workflowProcessInstanceId)
      return;
    if (item.itemType === 'content')
      this.service.approveContent(item.itemId).subscribe({ next: () => this.load() });
    else if (item.itemType === 'campaign')
      this.service.approveCampaign(item.itemId).subscribe({ next: () => this.load() });
  }

  openRejectDialog(item: GrowthReviewQueueItem): void {
    this.selectedRejectItem = item;
    this.rejectReason = '';
    this.rejectDialogVisible.set(true);
  }

  confirmReject(): void {
    if (!this.selectedRejectItem || this.selectedRejectItem.workflowProcessInstanceId)
      return;
    if (this.selectedRejectItem.itemType === 'content')
      this.service.rejectContent(this.selectedRejectItem.itemId, this.rejectReason || 'Rejected in review queue').subscribe({ next: () => this.load() });
    this.rejectDialogVisible.set(false);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
